/**
 * Main App component with routing
 * Performance optimized with lazy loading for all routes
 */

import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/Layout/AppShell';
import { useAuthStore } from './state/auth';
import { SkeletonTodayPage } from './components/ui/Skeleton';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { useAppHeight } from './lib/hooks';
import { DeepLinkHandler } from './components/DeepLinkHandler';
import { isNativeApp } from './lib/platform';

// Dev-only: Safe area debug overlay for mobile layout testing
// Toggle with Ctrl+Shift+S (or Cmd+Shift+S on Mac)
const SafeAreaDebugOverlay = lazy(() =>
  import('./components/dev/SafeAreaDebugOverlay').then(m => ({ default: m.SafeAreaDebugOverlay }))
);

// Dev-only: Layout debug overlay for iOS layout issues
// Activate with query param: ?debugLayout=1
const LayoutDebugOverlay = lazy(() =>
  import('./components/dev/LayoutDebugOverlay').then(m => ({ default: m.LayoutDebugOverlay }))
);

/**
 * Check if running on localhost and redirect to 127.0.0.1 for phone auth compatibility
 * This must happen at the app level, before any routing
 * SKIP for native apps - they use Capacitor Firebase plugin which doesn't need this redirect
 *
 * NOTE: The actual redirect happens synchronously in index.html to avoid React lifecycle issues.
 * This hook is kept as a safety net but should rarely trigger.
 */
function useLocalhostRedirect(): boolean {
  // Check synchronously on first render - if we're on localhost, we need to redirect
  // This avoids issues with React StrictMode double-mounting
  if (typeof window !== 'undefined' &&
      !isNativeApp() &&
      window.location.hostname === 'localhost' &&
      import.meta.env.DEV) {
    // Redirect immediately - this will cause the page to navigate away
    const newUrl = window.location.href.replace('localhost', '127.0.0.1');
    window.location.replace(newUrl);
    return true; // Return true to show loading state while redirecting
  }

  return false;
}



// Lazy load core routes for better code splitting
const Auth = lazy(() => import('./routes/Auth').then(m => ({ default: m.Auth })));
const Dashboard = lazy(() => import('./routes/Dashboard').then(m => ({ default: m.Dashboard })));
const Mindfulness = lazy(() => import('./routes/Mindfulness').then(m => ({ default: m.Mindfulness })));
const Learn = lazy(() => import('./routes/Learn').then(m => ({ default: m.Learn })));
const ActionDetail = lazy(() => import('./routes/ActionDetail').then(m => ({ default: m.ActionDetail })));
const Me = lazy(() => import('./routes/Me').then(m => ({ default: m.Me })));
const Milestones = lazy(() => import('./routes/Milestones').then(m => ({ default: m.Milestones })));

/**
 * Logo Mark - Used in loading screens
 * Botanical design representing growth and nurturing
 */
function LogoMark({ className = "w-12 h-12" }: { className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 64 64" className="w-full h-full animate-pulse">
        <defs>
          <linearGradient id="loadingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#38A3F5' }} />
            <stop offset="50%" style={{ stopColor: '#007AFF' }} />
            <stop offset="100%" style={{ stopColor: '#0C6DC4' }} />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="56" height="56" rx="14" ry="14" fill="url(#loadingGradient)" />
        <g transform="translate(32, 32)">
          <circle cx="0" cy="0" r="7" fill="white" opacity="0.95" />
          <path d="M 0 -8 C 6 -14, 12 -16, 12 -10 C 12 -4, 6 -2, 0 -8" fill="white" opacity="0.9" transform="rotate(0)" />
          <path d="M 0 -8 C 6 -14, 12 -16, 12 -10 C 12 -4, 6 -2, 0 -8" fill="white" opacity="0.85" transform="rotate(72)" />
          <path d="M 0 -8 C 6 -14, 12 -16, 12 -10 C 12 -4, 6 -2, 0 -8" fill="white" opacity="0.8" transform="rotate(144)" />
          <path d="M 0 -8 C 6 -14, 12 -16, 12 -10 C 12 -4, 6 -2, 0 -8" fill="white" opacity="0.85" transform="rotate(216)" />
          <path d="M 0 -8 C 6 -14, 12 -16, 12 -10 C 12 -4, 6 -2, 0 -8" fill="white" opacity="0.9" transform="rotate(288)" />
        </g>
      </svg>
    </div>
  );
}

// Lazy load partner pages
const SharedTaskListPage = lazy(() => import('./pages/SharedTaskListPage'));

// Lazy load baby tracker page
const BabyTrackerPage = lazy(() => import('./pages/BabyTrackerPage'));

// Lazy load baby insights page
const BabyInsightsPage = lazy(() => import('./pages/BabyInsightsPage'));

// Lazy load shared calendar page
const CalendarPage = lazy(() => import('./pages/CalendarPage'));

// Lazy load photos page
const PhotosPage = lazy(() => import('./pages/PhotosPage'));

// Lazy load documents page
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));

// Lazy load doctor Q&A page
const DoctorQaPage = lazy(() => import('./pages/DoctorQaPage'));

// Lazy load chat page
const ChatPage = lazy(() => import('./pages/ChatPage'));

// Lazy load Love Maps page
const LoveMapsPage = lazy(() => import('./pages/LoveMapsPage'));

// Lazy load Partner Hub page
const PartnerPage = lazy(() => import('./pages/PartnerPage'));

// Share landing page (public)
const SharePage = lazy(() => import('./pages/SharePage').then(m => ({ default: m.SharePage })));

// Public photos page (share link)
const PublicPhotosPage = lazy(() => import('./pages/PublicPhotosPage'));

// Dev-only: Layout test page for iOS WebView debugging
const LayoutTestPage = lazy(() => import('./pages/dev/LayoutTestPage'));

// Legal pages (public)
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

/**
 * Protected route wrapper
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isBootstrapReady } = useAuthStore();

  // Wait for both auth AND userDoc to be ready before making routing decisions
  // This prevents flicker where auth is ready but userDoc is still loading
  if (!isBootstrapReady()) {
    return (
      <div className="min-h-dvh bg-calm-50 px-4 max-w-lg mx-auto">
        <SkeletonTodayPage />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const { initialize } = useAuthStore();
  const isRedirecting = useLocalhostRedirect();

  // Sync --app-height CSS variable with viewport (critical for iOS Safari)
  useAppHeight();

  // Initialize auth on mount with proper cleanup
  useEffect(() => {
    const cleanup = initialize();
    return cleanup;
  }, [initialize]);

  // Dev-only: Register mobile layout audit in window for console access
  useEffect(() => {
    if (import.meta.env.DEV) {
      import('./lib/mobileLayoutTest').then(({ runMobileLayoutAudit }) => {
        (window as Window & { runMobileLayoutAudit?: typeof runMobileLayoutAudit }).runMobileLayoutAudit = runMobileLayoutAudit;
        console.log('📱 Mobile Layout Audit: window.runMobileLayoutAudit()');
        console.log('🔧 Safe Area Debug: Cmd/Ctrl+Shift+S to toggle overlay');
      });
    }
  }, []);


  // Show loading state during localhost -> 127.0.0.1 redirect
  if (isRedirecting) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-[#FAFBFF]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Redirecting for phone auth compatibility...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {/* Dev-only: Mobile layout debug overlay - toggle with Cmd/Ctrl+Shift+S */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <SafeAreaDebugOverlay />
        </Suspense>
      )}
      {/* Dev-only: iOS layout debug overlay - activate with ?debugLayout=1 */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <LayoutDebugOverlay />
        </Suspense>
      )}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <DeepLinkHandler />
        <Routes>
          {/* Public routes - Auth page with minimal loading state (skeleton already in HTML) */}
          <Route
            path="/auth"
            element={
              <Suspense fallback={null}>
                <Auth />
              </Suspense>
            }
          />

          {/* Legal pages - public */}
          <Route
            path="/privacy"
            element={
              <Suspense fallback={null}>
                <PrivacyPolicyPage />
              </Suspense>
            }
          />
          <Route
            path="/terms"
            element={
              <Suspense fallback={null}>
                <TermsOfServicePage />
              </Suspense>
            }
          />

        {/* Protected routes with app shell */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<Suspense fallback={<SkeletonTodayPage />}><Dashboard /></Suspense>} />
          <Route path="/mindfulness" element={<Suspense fallback={<SkeletonTodayPage />}><Mindfulness /></Suspense>} />
          <Route path="/learn" element={<Suspense fallback={<SkeletonTodayPage />}><Learn /></Suspense>} />
          <Route path="/milestones" element={<Suspense fallback={<SkeletonTodayPage />}><Milestones /></Suspense>} />
          <Route path="/action/:actionId" element={<Suspense fallback={<SkeletonTodayPage />}><ActionDetail /></Suspense>} />
          <Route path="/me" element={<Suspense fallback={<SkeletonTodayPage />}><Me /></Suspense>} />
          {/* Shared tasks route */}
          <Route
            path="/tasks"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <SharedTaskListPage />
              </Suspense>
            }
          />
          {/* Baby Tracker route */}
          <Route
            path="/tracker"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <BabyTrackerPage />
              </Suspense>
            }
          />
          {/* Baby Insights route */}
          <Route
            path="/insights"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <BabyInsightsPage />
              </Suspense>
            }
          />
          {/* Shared Calendar route */}
          <Route
            path="/calendar"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <CalendarPage />
              </Suspense>
            }
          />
          {/* Photos route */}
          <Route
            path="/photos"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <PhotosPage />
              </Suspense>
            }
          />
          {/* Documents route */}
          <Route
            path="/documents"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <DocumentsPage />
              </Suspense>
            }
          />
          {/* Doctor Q&A routes */}
          <Route
            path="/doctor-qa"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <DoctorQaPage />
              </Suspense>
            }
          />
          {/* Chat routes */}
          <Route
            path="/chat"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <ChatPage />
              </Suspense>
            }
          />
          <Route
            path="/chat/:sessionId"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <ChatPage />
              </Suspense>
            }
          />
          {/* Love Maps route */}
          <Route
            path="/love-maps"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <LoveMapsPage />
              </Suspense>
            }
          />
          {/* Partner Hub route */}
          <Route
            path="/partner"
            element={
              <Suspense fallback={<SkeletonTodayPage />}>
                <PartnerPage />
              </Suspense>
            }
          />
        </Route>

        {/* Public share landing page - for link previews */}
        <Route
          path="/share"
          element={
            <Suspense fallback={
              <div className="min-h-dvh bg-[#FAFBFF] flex items-center justify-center">
                <LogoMark className="w-14 h-14" />
              </div>
            }>
              <SharePage />
            </Suspense>
          }
        />

        {/* Public photos share page - no authentication required */}
        <Route
          path="/photos/share/:token"
          element={
            <Suspense fallback={
              <div className="min-h-dvh bg-[#FAFBFF] flex items-center justify-center">
                <LogoMark className="w-14 h-14" />
              </div>
            }>
              <PublicPhotosPage />
            </Suspense>
          }
        />

        {/* Dev-only: Layout test page for iOS WebView debugging */}
        {import.meta.env.DEV && (
          <Route
            path="/dev/layout"
            element={
              <Suspense fallback={null}>
                <LayoutTestPage />
              </Suspense>
            }
          />
        )}

        {/* Catch all - redirect to today */}
        <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
